import { useState } from 'react';

import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useDeleteQualityConfig, useQualityConfigList } from '@/hooks/useQualityConfigs';

export const QualityConfigsListPage = () => {
  usePageTitle('Quality Configs');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: configs, isLoading } = useQualityConfigList();
  const deleteMutation = useDeleteQualityConfig();
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast({
        variant: 'success',
        title: 'Deleted',
        description: `'${deleteTarget.name}' removed.`,
      });
      setDeleteTarget(null);
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to delete config.' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>Quality Configs</CardTitle>
            <CardDescription>
              Per-DB configurations for the QualityAgent. Quality assessment uses transcript +
              audio. Without an audio recording, the audio-only fields (silence, accent, audio
              glitches) come back null.
            </CardDescription>
          </div>
          <Button onClick={() => navigate('/quality-configs/new')}>
            <Plus className="mr-2 h-4 w-4" /> New config
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Rubric version</TableHead>
                <TableHead>Default?</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(configs ?? []).map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="font-mono text-xs">{c.model_name}</TableCell>
                  <TableCell className="font-mono text-xs">{c.rubric_version}</TableCell>
                  <TableCell>
                    {c.is_default ? (
                      <Badge>default</Badge>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {new Date(c.updated_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/quality-configs/${c.id}`)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteTarget({ id: c.id, name: c.name })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        resourceName="quality config"
        resourceIdentifier={deleteTarget?.name}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};
